import {Component, OnInit, OnChanges, DoCheck, Input, Output, EventEmitter, SimpleChanges, Type} from '@angular/core';
import { Row, ItemClicked } from '../../models/models';
import { SpreadingState } from '../../state/manager';
import { RoundingService } from '@services/rounding.service';
import {SubmissionRuleActions} from "@services/template-data.service";

@Component({
    selector: 'app-spreading-template-row',
    templateUrl: './template-row.component.html',
    styleUrls: ['./template-row.component.scss'],
    standalone: false
})
export class TemplateRowComponent implements OnInit, OnChanges, DoCheck {

  @Input() row: Row;
  @Input() state: SpreadingState;
  @Input() selectedColumn: number;
  @Input() enableGoalSeek: boolean;
  @Input() columnCheckOk: boolean;
  @Input() viewInNativeLanguage: boolean;
  @Input() goalSeekLineItem: number;
  @Input() visibleSuggestionsOriginId?: number;
  @Input() goalSeekCount?: number;
  /** True for categorized source rows under an expanded collapsible parent (AUTO-2986). */
  @Input() isNestedTemplateRow = false;
  @Output() clearedCategorization = new EventEmitter<Row>();
  @Output() goalSeek = new EventEmitter<Row>();
  @Output() hideSuggestions = new EventEmitter<any>();
  @Output() goToColumn = new EventEmitter<any>();

  childrenCollapsable = false;

  sourceChildren: Row[] = []; // rows of source mapped items
  subChildren: Row[] = []; // other rows/subitems

  isGoalSeekToolTipVisible: boolean = false;
  isRowHidden = false;

  columnDatesSummary: string[] = [];

  constructor(private _roundingService: RoundingService) { }

  ngOnInit() {
    if (this.row?.isDynamicAdjustmentStatement){
      this.isRowHidden = true;
    }
    // Hide rows that are marked as hidden from view (only for StandardItem and GenericStandardItem)
    if (this.row?.isHiddenFromView){
      this.isRowHidden = true;
    }
    this.setRowChildrenRelatedProperties();
    this.columnDatesSummary = this.state?.getColumnDatesSummary() || [];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.row) {
      this.setRowChildrenRelatedProperties();
    }

    if (changes?.state && changes?.state.hasOwnProperty('columns') && this.state) {
      this.columnDatesSummary = this.state.getColumnDatesSummary();
    }
  }

  ngDoCheck() {
    // This will handle the case when property is deleted which ngOnChanges won't be called.
    const shouldBeCollapsable = !this.row.header && this.row.hasOwnProperty('children');
    if (this.childrenCollapsable !== shouldBeCollapsable) {
      this.setRowChildrenRelatedProperties();
    }
  }

  removeCategorization(evt, row: Row) {
    evt.preventDefault();
    this.clearedCategorization.emit(row);
  }

  hideGoalSeek(evt) {
    evt.preventDefault();
    this.hideSuggestions.emit();
    this.isGoalSeekToolTipVisible = false;
  }

  startGoalSeek(evt, row: Row) {
    evt.preventDefault();
    this.goalSeek.emit(row);
    this.isGoalSeekToolTipVisible = true;
  }

  removeCategorizationChild(row: Row) {
    this.clearedCategorization.emit(row);
  }

  rowIsValid(row) {
    return row.cells.every(c => {
      return !c.flag;
    });
  }

  errors(): Array<string> {
    return this.row.validationRow.cells.filter(c => c.flag).map(c => c.flagReason);
  }

  getSourceItems() {
    if (!this.row.children) {
      return [];
    }
    return this.row.children.filter(child => !child.template);
  }

  /*
   * Get rounded value from display rounding/multiplication
   */
  getRoundedValue(cellValue: number, row: Row) {
    const roundedVal = this._roundingService.roundCellValue(this.state, row, cellValue);
    return roundedVal;
  }

  emitGoToColumn(columnIndex: number) {
    this.goToColumn.emit(columnIndex);
  }

  protected readonly SubmissionRuleActions = SubmissionRuleActions;

  private setRowChildrenRelatedProperties(): void {
    this.childrenCollapsable = !this.row.header && this.row.hasOwnProperty('children');
    if (this.row.children) {
      this.sourceChildren = this.row.children.filter(child => !child.template);
      this.subChildren = this.row.children.filter(child => !!child.template);
    } else {
      this.sourceChildren = [];
      this.subChildren = [];
    }
  }
}
